import authService, { TOKEN_TYPES } from "./authService";

const BASE_API_URL = import.meta.env.VITE_API_URL;

interface ChatError extends Error {
  status?: number;
  responseData?: unknown;
}

export const chatService = {
  sendMessage: async (messageInfo: unknown): Promise<unknown> => {
    const token = authService.getToken(TOKEN_TYPES.ACCESS);
    const response = await fetch(`${BASE_API_URL}/askvivo/prompt/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(messageInfo),
    });

    const responseData = await response.json().catch(() => null);

    if (!response.ok) {
      const error: ChatError = new Error(
        responseData?.message || "Failed to send message",
      );
      error.status = response.status;
      error.responseData = responseData;
      throw error;
    }
    return responseData;
  },

  deleteHistoryItem: async (itemId: string | number): Promise<unknown> => {
    const token = authService.getToken(TOKEN_TYPES.ACCESS);
    const response = await fetch(`${BASE_API_URL}/askvivo/history/${itemId}/`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error("Failed to delete history item");
    }
    return response.json();
  },

  /**
   * List the current user's chat sessions for the scope they are viewing.
   *
   * The backend scopes history to the request's client on its own; the query
   * string only selects *within* that client. In portfolio scope there is no
   * trial to filter by (`id` is a route segment, not a trial id), so ask for
   * portfolio sessions explicitly — sending an unrecognised parameter such as
   * `portfolio_id` returns every session the user has in every client.
   */
  fetchHistory: async (
    id: string | number,
    scope = "trial",
  ): Promise<unknown> => {
    const token = authService.getToken(TOKEN_TYPES.ACCESS);
    const query =
      scope === "portfolio"
        ? "scope=portfolio"
        : `trial_id=${encodeURIComponent(String(id))}`;

    const response = await fetch(`${BASE_API_URL}/askvivo/history/?${query}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      throw new Error("Failed to fetch chat history");
    }
    return response.json();
  },

  getHistoryDetails: async (chatId: string | number): Promise<unknown> => {
    const token = authService.getToken(TOKEN_TYPES.ACCESS);
    const response = await fetch(`${BASE_API_URL}/askvivo/history/${chatId}/`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      throw new Error("Failed to fetch history details");
    }
    return response.json();
  },
};
